package dev.mmartos.advent.screen.day07

import androidx.compose.runtime.Immutable
import dev.mmartos.advent.common.BaseViewModel
import dev.mmartos.advent.common.ErrorStage
import dev.mmartos.advent.common.ParsedStage
import dev.mmartos.advent.common.ParsingStage
import dev.mmartos.advent.common.SolvedStage
import dev.mmartos.advent.common.SolverPart
import dev.mmartos.advent.common.SolvingStage
import dev.mmartos.advent.common.UiState
import dev.mmartos.advent.utils.Delay.longDelay
import dev.mmartos.advent.utils.Delay.regularDelay
import dev.mmartos.advent.utils.Delay.tinyDelay
import dev.mmartos.advent.utils.DelayReason
import kotlin.math.max
import kotlin.math.roundToLong
import kotlinx.collections.immutable.PersistentList
import kotlinx.collections.immutable.PersistentMap
import kotlinx.collections.immutable.PersistentSet
import kotlinx.collections.immutable.persistentMapOf
import kotlinx.collections.immutable.toPersistentList
import kotlinx.collections.immutable.toPersistentMap
import kotlinx.collections.immutable.toPersistentSet
import dev.mmartos.advent.common.ParserStage as BaseParserStage
import dev.mmartos.advent.common.SolverStage as BaseSolverStage

@Immutable
data class TachyonDiagram(
    val cols: Int,
    val rows: Int,
    val start: Pair<Int, Int>,
    val content: PersistentList<CharArray>,
)

sealed class Timeline {
    data object Straight : Timeline()
    data object SlipLeft : Timeline()
    data object SlipRight : Timeline()
}

sealed class ParserStage : BaseParserStage {
    data class Parsing(
        val currentLine: String,
        val partialTachyonDiagram: TachyonDiagram,
    ) : ParserStage(), ParsingStage

    data class Parsed(
        val tachyonDiagram: TachyonDiagram,
    ) : ParserStage(), ParsedStage

    data object Error : ParserStage(), ErrorStage
}

sealed class SolverStage1 : BaseSolverStage {
    override fun solverPart(): SolverPart = SolverPart.SOLVER_PART_1

    data class Solving(
        val tachyonDiagram: TachyonDiagram,
        val activeBeams: PersistentSet<Pair<Int, Int>>,
        val activeSplits: PersistentSet<Pair<Int, Int>>,
        val partialSolution: String,
    ) : SolverStage1(), SolvingStage

    data class Solved(
        val tachyonDiagram: TachyonDiagram,
        val activeBeams: PersistentSet<Pair<Int, Int>>,
        val activeSplits: PersistentSet<Pair<Int, Int>>,
        val solution: String
    ) : SolverStage1(), SolvedStage
}

sealed class SolverStage2 : BaseSolverStage {
    override fun solverPart(): SolverPart = SolverPart.SOLVER_PART_2

    data class Solving(
        val tachyonDiagram: TachyonDiagram,
        val beams: PersistentSet<Pair<Int, Int>>,
        val beamTimeline: PersistentMap<Pair<Int, Int>, Timeline>,
        val partialSolution: String,
    ) : SolverStage2(), SolvingStage

    data class Solved(
        val tachyonDiagram: TachyonDiagram,
        val beams: PersistentSet<Pair<Int, Int>>,
        val solution: String
    ) : SolverStage2(), SolvedStage
}

typealias Day07UiState = UiState<ParserStage, SolverStage1, SolverStage2>

class Day07ViewModel : BaseViewModel<ParserStage, TachyonDiagram, SolverStage1, SolverStage2>() {

    override val Day07UiState.parsedData: TachyonDiagram?
        get() = (uiState.value.parserStage as? ParserStage.Parsed)?.tachyonDiagram

    fun startParser(input: List<String>) = doParsing {
        val content = mutableListOf<CharArray>()
        var start = -1 to -1
        runCatching {
            input.forEachIndexed { index, line ->
                if (start.first == -1 && line.contains('S')) {
                    start = index to line.indexOf('S')
                }
                content += line.toCharArray()
                uiStateUpdater.update {
                    it.copy(
                        parserStage = ParserStage.Parsing(
                            currentLine = line,
                            partialTachyonDiagram = TachyonDiagram(
                                cols = content.first().size,
                                rows = content.size,
                                start = start,
                                content = content.toPersistentList(),
                            ),
                        ),
                    )
                }
                longDelay(DelayReason.Parser)
            }
            if (!content.isValid()) {
                error("Invalid input")
            }
        }.onFailure {
            uiStateUpdater.update {
                it.copy(
                    parserStage = ParserStage.Error,
                )
            }
        }.onSuccess {
            uiStateUpdater.update {
                it.copy(
                    parserStage = ParserStage.Parsed(
                        tachyonDiagram = TachyonDiagram(
                            cols = content.first().size,
                            rows = content.size,
                            start = start,
                            content = content.toPersistentList(),
                        ),
                    ),
                )
            }
            solvePart1()
            solvePart2()
        }
    }

    fun solvePart1() = doSolving {
        var result = 0L
        uiState.value.parsedData?.run {
            val activeBeams = mutableSetOf<Pair<Int, Int>>()
            var currentBeams = mutableSetOf(start)
            val activeSplits = mutableSetOf<Pair<Int, Int>>()
            val range = start.first until rows - 1
            repeat(range.count()) {
                currentBeams = currentBeams.flatMap { beamPosition ->
                    val cellPosition = beamPosition.copy(first = beamPosition.first + 1)
                    val cellContent = content[cellPosition.first][cellPosition.second]
                    when (cellContent) {
                        '^' -> {
                            activeSplits.add(cellPosition)
                            result++
                            setOf(
                                cellPosition.first to cellPosition.second - 1,
                                cellPosition.first to cellPosition.second + 1,
                            )
                        }

                        else -> {
                            activeBeams.add(cellPosition)
                            setOf(cellPosition)
                        }
                    }
                }.toMutableSet()
                uiStateUpdater.update {
                    it.copy(
                        solverStage1 = SolverStage1.Solving(
                            tachyonDiagram = this,
                            activeBeams = activeBeams.toPersistentSet(),
                            activeSplits = activeSplits.toPersistentSet(),
                            partialSolution = result.toString(),
                        )
                    )
                }
                regularDelay(DelayReason.Solver)
            }
            uiStateUpdater.update {
                it.copy(
                    solverStage1 = SolverStage1.Solved(
                        tachyonDiagram = this,
                        activeBeams = activeBeams.toPersistentSet(),
                        activeSplits = activeSplits.toPersistentSet(),
                        solution = result.toString(),
                    )
                )
            }
        }
    }

    fun solvePart2() = doSolving {
        var result = 0L
        uiState.value.parsedData?.run {
            val activeBeams = mutableSetOf<Pair<Int, Int>>()
            val activeSplits = mutableSetOf<Pair<Int, Int>>()
            val dpMap = Array(rows) { LongArray(cols) { 0L } }
            dpMap[start.first][start.second] = 1L
            for (curRow in start.first until rows - 1) {

                for (curCol in 0 until cols) {
                    val ways = dpMap[curRow][curCol]
                    if (ways == 0L) continue

                    val currentCell = content[curRow + 1][curCol]
                    when (currentCell) {
                        '^' -> {
                            activeSplits.add(curRow + 1 to curCol)
                            dpMap[curRow + 1][curCol - 1] += ways
                            dpMap[curRow + 1][curCol + 1] += ways
                        }

                        else -> {
                            activeBeams.add(curRow + 1 to curCol)
                            dpMap[curRow + 1][curCol] += ways
                        }
                    }
                }
            }
            val targetCols = dpMap[rows - 1].withIndex().filter { it.value != 0L }.map { it.index }
            for (currentCol in targetCols) {
                val prevResult = result
                val maxSteps = max(dpMap[rows - 1][currentCol].coerceAtMost(cols.toLong()).toInt(), cols)
                val nextResult = prevResult + dpMap[rows - 1][currentCol]
                val stepResult = (nextResult - prevResult).toDouble() / maxSteps
                repeat(maxSteps) { curStep ->
                    var timelineRow = rows - 1
                    var timelineCol = currentCol
                    val timeline = buildMap {
                        put(timelineRow to timelineCol, Timeline.Straight)
                        while (timelineRow != start.first) {
                            val currentRow = timelineRow - 1
                            val candidates = buildMap {
                                if ((timelineCol - 1) >= 0 && content[currentRow][timelineCol - 1] == '^' && activeSplits.contains(
                                        currentRow to timelineCol - 1
                                    )
                                ) {
                                    put(currentRow to (timelineCol - 1), Timeline.SlipRight)
                                }
                                if (dpMap[currentRow][timelineCol] != 0L && (currentRow % 2 != 0)) {
                                    put(currentRow to timelineCol, Timeline.Straight)
                                }
                                if ((timelineCol + 1) < cols && content[currentRow][timelineCol + 1] == '^' && activeSplits.contains(
                                        currentRow to timelineCol + 1
                                    )
                                ) {
                                    put(currentRow to (timelineCol + 1), Timeline.SlipLeft)
                                }
                            }
                            if (candidates.isNotEmpty()) {
                                val candidate = candidates.entries.toList().shuffled().first()
                                put(candidate.key, candidate.value)
                                timelineRow = candidate.key.first
                                timelineCol = candidate.key.second
                            } else {
                                timelineRow = currentRow
                                put(timelineRow to timelineCol, Timeline.Straight)
                            }
                        }
                    }
                    uiStateUpdater.update {
                        it.copy(
                            solverStage2 = SolverStage2.Solving(
                                tachyonDiagram = this,
                                beams = activeBeams.toPersistentSet(),
                                beamTimeline = timeline.toPersistentMap(),
                                partialSolution = (prevResult + stepResult * curStep).roundToLong().toString(),
                            )
                        )
                    }
                    tinyDelay(DelayReason.Solver)
                }
                result = nextResult
                uiStateUpdater.update {
                    it.copy(
                        solverStage2 = SolverStage2.Solving(
                            tachyonDiagram = this,
                            beams = activeBeams.toPersistentSet(),
                            beamTimeline = persistentMapOf(),
                            partialSolution = result.toString(),
                        )
                    )
                }
                tinyDelay(DelayReason.Solver)
            }
            uiStateUpdater.update {
                it.copy(
                    solverStage2 = SolverStage2.Solved(
                        tachyonDiagram = this,
                        beams = activeBeams.toPersistentSet(),
                        solution = result.toString(),
                    )
                )
            }
        }
    }

    private val regex = Regex("^[.\\^S]+$")

    private fun List<CharArray>.isValid(): Boolean =
        all { it.isValid() } && with(map { it.size }.toSet()) { size == 1 }

    private fun CharArray.isValid(): Boolean =
        regex.matches(this.joinToString(""))
}